import {
  FC, ReactNode, useId, useState,
} from 'react'
import { useDropzone } from 'react-dropzone'
import UiIcon from '../Icon'
import { MAX_FILE_SIZE_BYTES, PDF_ACCEPT, resolveDroppedFile } from './logic'

type FileFieldProps = {
  label: string
  name: string
  file: File | null
  onFileSelect: (file: File) => void
  hint?: ReactNode
  error?: string | null
  isOptional?: boolean
  className?: string
}

type FieldState = 'idle' | 'received' | 'dragActive' | 'dragReject' | 'error'

// Le fond est porté par chaque état (pas par la base) : deux utilitaires `bg-*` sur le même élément
// se départagent par l'ordre de la feuille Tailwind, pas par l'ordre des classes.
const ROW_BASE = 'flex flex-col gap-3 rounded-xl border px-[18px] py-[15px] font-aeonik outline-none transition-colors sm:flex-row sm:items-center sm:gap-3.5'

const FOCUS_RING = 'focus-visible:border-solid focus-visible:border-ink focus-visible:shadow-[0_0_0_3px_rgba(29,30,33,0.08)]'

const ROW_STATE_CLASSES: Record<FieldState, string> = {
  idle: `cursor-pointer border-dashed border-ui-gray-500 bg-white hover:border-ink hover:bg-cream/40 ${FOCUS_RING}`,
  received: `cursor-pointer border-solid border-positive/30 bg-positive-bg hover:border-positive ${FOCUS_RING}`,
  dragActive: 'border-solid border-ink bg-cream',
  dragReject: 'border-solid border-coral bg-white',
  error: `cursor-pointer border-solid border-coral bg-white ${FOCUS_RING}`,
}

const CUE_DISC_CLASSES: Record<FieldState, string> = {
  idle: 'bg-ui-gray-50 text-ink',
  received: 'bg-white text-positive',
  dragActive: 'bg-white text-ink',
  dragReject: 'bg-white text-coral',
  error: 'bg-ui-gray-50 text-ink',
}

const CUE_TEXTS: Record<FieldState, { title: string, subtitle: string, titleClass: string }> = {
  idle: { title: 'Glissez-déposez votre PDF ici', subtitle: 'ou cliquez pour parcourir', titleClass: 'text-ink' },
  received: { title: 'Fichier sélectionné', subtitle: 'Glissez ou cliquez pour remplacer', titleClass: 'text-positive' },
  dragActive: { title: 'Déposez pour sélectionner', subtitle: 'PDF, 20 Mo max', titleClass: 'text-ink' },
  dragReject: { title: 'Fichier non accepté', subtitle: 'PDF uniquement, 20 Mo max', titleClass: 'text-coral' },
  error: { title: 'Glissez-déposez votre PDF ici', subtitle: 'ou cliquez pour parcourir', titleClass: 'text-ink' },
}

type FieldStateInputs = {
  isDragActive: boolean
  isDragReject: boolean
  hasError: boolean
  hasFile: boolean
}

const resolveFieldState = ({
  isDragActive, isDragReject, hasError, hasFile,
}: FieldStateInputs): FieldState => {
  if (isDragReject) return 'dragReject'
  if (isDragActive) return 'dragActive'
  if (hasError) return 'error'
  if (hasFile) return 'received'
  return 'idle'
}

// Carte de dépôt PDF contrôlée par le parent (`file`/`onFileSelect`), même rendu que la ligne
// `.up-row` de l'écran Justificatifs (`CreationForm/Justifs/JustifRow.tsx`) mais sans envoi
// immédiat : le fichier est simplement retenu, l'upload a lieu à la soumission du formulaire. Le
// message d'erreur (contrôle local ou erreur du parent) reste sous la carte, hors zone de dépôt.
const FileField: FC<FileFieldProps> = ({
  label, name, file, onFileSelect, hint, error = null, isOptional = false, className = '',
}) => {
  const [localError, setLocalError] = useState<string | null>(null)
  const hintId = useId()
  const errorId = useId()

  const {
    getRootProps, getInputProps, isDragActive, isDragReject,
  } = useDropzone({
    accept: PDF_ACCEPT,
    maxSize: MAX_FILE_SIZE_BYTES,
    multiple: false,
    onDrop: (acceptedFiles, fileRejections) => {
      const outcome = resolveDroppedFile(acceptedFiles, fileRejections)
      if (outcome.kind === 'error') {
        setLocalError(outcome.message)
        return
      }
      setLocalError(null)
      onFileSelect(outcome.file)
    },
  })

  const displayedError = localError ?? error
  const fieldState = resolveFieldState({
    isDragActive, isDragReject, hasError: Boolean(displayedError), hasFile: Boolean(file),
  })
  const cue = CUE_TEXTS[fieldState]
  const describedBy = [hint ? hintId : null, displayedError ? errorId : null].filter(Boolean).join(' ')

  return (
    <div className={`mt-3 flex flex-col gap-1.5 ${className}`}>
      <span className="font-aeonik text-[13.5px] font-medium text-ink">
        {label}
        {isOptional && <span className="ml-1.5 font-normal text-ui-gray-700">(facultatif)</span>}
      </span>
      <div
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...getRootProps({
          role: 'button',
          'aria-label': `${file ? 'Remplacer' : 'Sélectionner'} : ${label}`,
          'aria-describedby': describedBy || undefined,
          className: `${ROW_BASE} ${ROW_STATE_CLASSES[fieldState]}`,
        })}
      >
        {/* eslint-disable-next-line react/jsx-props-no-spreading */}
        <input {...getInputProps({ id: name, name })} />
        <div className="min-w-0 flex-1">
          {file ? (
            <b className="block truncate text-[14.5px] font-medium text-ink">{file.name}</b>
          ) : null}
          {hint ? (
            <small id={hintId} className="mt-0.5 block text-[12.5px] text-ui-gray-700">{hint}</small>
          ) : null}
        </div>

        <div className="flex min-h-[44px] flex-none items-center gap-2.5 sm:min-h-0 sm:max-w-[230px]">
          <span
            className={`flex h-9 w-9 flex-none items-center justify-center rounded-full ${CUE_DISC_CLASSES[fieldState]}`}
          >
            <UiIcon name={fieldState === 'received' ? 'check' : 'upload'} size={16} />
          </span>
          <span className="text-[12.5px] leading-4 text-ui-gray-700">
            <b className={`block text-[13px] font-medium ${cue.titleClass}`}>{cue.title}</b>
            {cue.subtitle}
          </span>
        </div>
      </div>
      {displayedError ? (
        <p id={errorId} role="alert" className="font-aeonik text-xs text-[#8C2B22]">{displayedError}</p>
      ) : null}
    </div>
  )
}

export default FileField
